<template>
  <ApolloQuery
    :query="ticketQuery"
    :variables="{id: ticketId}"
    class="apollo-container"
  >
    <template #default="{result: {data}, query, isLoading}">
      <ApolloSubscribeToMore
        :document="ticketUpdatedSubscriptionQuery"
        :update-query="(_,{subscriptionData}) => {
          if (subscriptionData.data.ticketUpdated===ticketId) query.refetch()
        }"
      />
      <el-container
        v-show="isLoading"
        v-loading="isLoading"
        class="centered-preloader"
      />

      <el-container
        v-if="data && data.viewTicket"
        class="main-box"
      >
        <el-header>
          <el-row class="title-part-first-row">
            <el-col :span="22">
              <h1>
                #{{ ticketId }} | {{ data.viewTicket.title }}
                <el-badge
                  :value="internationalizeBadgeType(data.viewTicket.status)"
                  class="item"
                  :type="statusBadgeType(data.viewTicket.status)"
                  size="medium"
                />
              </h1>
            </el-col>

            <el-col
              :span="2"
              class="status-toggle-button-box"
            >
              <ApolloMutation
                :mutation="ChangeTicketStatusQuery"
                :variables="{
                  ticketId,
                  status: getNewStatus(data.viewTicket.status)
                }"
                tag="span"
                @done="query.refetch()"
              >
                <template #default="{mutate, loading, error}">
                  <el-tooltip
                    :content="$t('ticket status') +' (' + internationalizeBadgeType(data.viewTicket.status) + ')'"
                    placement="right"
                  >
                    <el-switch
                      :value="getBoolTicketStatus(data.viewTicket.status)"
                      :disabled="loading"
                      active-color="#729CC8"
                      inactive-color="#E8A1A1"
                      @change="mutate"
                    />
                  </el-tooltip>
                  <span v-if="error">{{ error }}</span>
                </template>
              </ApolloMutation>
            </el-col>
          </el-row>
          <el-row>
            <el-col :span="24">
              <span
                v-if="data.viewTicket.requester"
                class="ticket-info"
              >
                {{ formatDate(data.viewTicket.created_at) }}
                <img
                  src="@/assets/avatar.png"
                  width="25px"
                  class="img-thumbnail"
                > {{ data.viewTicket.requester.name }}
              </span>
            </el-col>
          </el-row>

          <el-divider class="title-bottom-line" />

          <el-row class="title-part-second-row">
            <el-col
              :span="24"
              class="view-order-button-box"
              style="text-align: right;"
            >
              <ApolloQuery
                :query="packageByPackageNumberQuery"
                :variables="{package_number: packageDetailsFromTags(data.viewTicket.tags, 'PACKAGE_NUMBER')}"
              >
                <template #default="packageByPackageNumberQuery">
                  <router-link
                    v-if="packageByPackageNumberQuery.result.data && packageByPackageNumberQuery.result.data.packageByPackageNumber"
                    :to="`/package/${packageByPackageNumberQuery.result.data.packageByPackageNumber.id}`"
                  >
                    <el-button
                      v-if="packageInfo.packageNumber"
                      type="text"
                      class="view-order-button"
                    >
                      {{ $t('View order') }} #{{ packageInfo.packageNumber }}
                    </el-button>
                  </router-link>
                </template>
              </apolloquery>
            </el-col>
          </el-row>
        </el-header>

        <el-main>
          <el-container class="details-box">
            <el-card
              shadow="never"
              class="ticket-body"
              style="white-space: pre-wrap"
            >
              {{ data.viewTicket.body }}
            </el-card>

            <!-- --------------------- TAGS START ------------------------------------ -->
            <ApolloMutation
              :mutation="updateTicketTagsQuery"
              :variables="updateTicketTagsArgs"
              @done="query.refetch()"
            >
              <template #default="{mutate, loading, error}">
                <el-collapse
                  v-model="ticketTagsCollapse"
                  v-loading="loading"
                  class="collapse-box"
                >
                  <el-collapse-item
                    :title="$t('Tags') + ' (' + data.viewTicket.tags.length + ')'"
                    name="1"
                  >
                    <el-container>
                      <el-main
                        class="collapse-content ticket-tags-box"
                      >
                        <p v-if="error">
                          {{ $t('An error occurred') }}: {{ error }}
                        </p>
                        <el-row>
                          <el-col>
                            <el-tag
                              v-for="tag in convertAnyToSupportTicket(data.viewTicket).tags"
                              :key="tag.id"
                              :closable="getBoolTicketStatus(data.viewTicket.status)"
                              class="ticket-tags mr-1"
                              size="small"
                              @close="handleDeleteTag(data.viewTicket.tags, tag.name); mutate();"
                            >
                              {{ tag.name }}
                            </el-tag>

                            <el-tooltip
                              :content="$t('Add tag')"
                              placement="right"
                            >
                              <el-link
                                v-if="getBoolTicketStatus(data.viewTicket.status)"
                                v-show="!newTagInputVisible"
                                class="ml-1"
                                icon="el-icon-plus"
                                @click="showNewTagInput"
                              />
                            </el-tooltip>
                          </el-col>
                        </el-row>

                        <el-row>
                          <el-col
                            :offset="24"
                            :span="24"
                          />
                        </el-row>

                        <section
                          :span="24"
                          class="add-tags-box"
                        >
                          <el-select
                            v-if="newTagInputVisible"
                            ref="newTagInput"
                            v-model="newTagsInputValues"
                            class="new-tag-input"
                            multiple
                            filterable
                            remote
                            size="mini"
                            reserve-keyword
                            :remote-method="fetchTags"
                            :loading="isLoadingTags"
                            :placeholder="$t('Enter tags for the ticket')"
                          >
                            <el-option
                              v-for="item in tagsOptions"
                              :key="item.value"
                              :label="item.label"
                              :value="item.value"
                            />
                          </el-select>

                          <el-button-group
                            v-if="newTagInputVisible"
                            class="new-tag-buttons"
                          >
                            <el-button
                              type="success"
                              icon="el-icon-check"
                              circle
                              :disabled="!newTagsInputValues.length"
                              class="confirm-new-tags-button"
                              @click="handleNewTagInputConfirm(data.viewTicket.tags,mutate);"
                            />

                            <el-button
                              type="success"
                              icon="el-icon-close"
                              circle
                              class="close-new-tags-button"
                              @click="() => newTagInputVisible = false"
                            />
                          </el-button-group>
                        </section>
                      </el-main>
                    </el-container>
                  </el-collapse-item>
                </el-collapse>
              </template>
            </ApolloMutation>
            <!-- --------------------- TAGS END ------------------------------------ -->

            <!-- --------------------- PACKAGE DETAILS START ------------------------------------ -->
            <PackageDetails
              v-if="packageInfo.packageNumber"
              :package-number="packageDetailsFromTags(data.viewTicket.tags, 'PACKAGE_NUMBER')"
              @result="handlePackageResult"
            />
            <!-- --------------------- PACKAGE DETAILS START ------------------------------------ -->

            <!-- --------------------- ATTACHMENTS START ------------------------------------ -->
            <el-collapse
              v-if="mergeAttachments(data.viewTicket.comments).length > 0"
              v-model="attachmentsCollapse"
              class="collapse-box"
            >
              <el-collapse-item
                :title="$t('Attachments') + ' (' + mergeAttachments(data.viewTicket.comments).length + ')'"
                name="1"
              >
                <el-container>
                  <el-main
                    class="collapse-content"
                  >
                    <attachments-list :attachments="mergeAttachments(data.viewTicket.comments)" />
                  </el-main>
                </el-container>
              </el-collapse-item>
            </el-collapse>
            <!-- --------------------- ATTACHMENTS END ------------------------------------ -->
          </el-container>

          <el-container
            v-if="data.viewTicket.comments.length > 0"
            class="ticket-comment-list"
          >
            <section
              v-for="comment in convertAnyToSupportTicket(data.viewTicket).comments.slice().reverse()"
              :key="comment.id"
              class="mt-2"
            >
              <el-row
                v-if="comment.author.email===getUserEmail"
                class="mb-1"
              >
                <el-col
                  v-if="!(comment === data.viewTicket.comments[data.viewTicket.comments.length - 1] && comment.body === 'attachments')"
                  :xs="24"
                >
                  <TicketCommentSelf :comment="comment" />
                </el-col>
              </el-row>
              <el-row
                v-else
                class="my-1"
              >
                <el-col
                  v-if="!(comment === data.viewTicket.comments[data.viewTicket.comments.length - 1] && comment.body === 'attachments')"
                  :xs="24"
                >
                  <TicketCommentDistant :comment="comment" />
                </el-col>
              </el-row>
            </section>
          </el-container>
        </el-main>

        <el-footer v-if="getBoolTicketStatus(data.viewTicket.status)">
          <ApolloMutation
            :mutation="CreateTicketCommentQuery"
            :variables="{ticketId,...form}"
            :context="{
              hasUpload: true
            }"
            @done="() => {
              query.refetch(); resetForm();
            }"
          >
            <template #default="{mutate, loading, error}">
              <div
                v-loading="loading"
                class="add-new-comment-box"
              >
                <el-divider class="new-comment-line" />

                <el-row
                  :gutter="5"
                  class="comment-attachment-box"
                >
                  <el-col
                    :xs="24"
                    :sm="14"
                    :md="15"
                    :lg="16"
                    class="comment-box"
                  >
                    <span v-if="error">{{ error }}</span>
                    <el-input
                      v-model="form.comment"
                      type="textarea"
                      resize="none"
                      :rows="2"
                      :placeholder="$t('Comment description')"
                      style="height: 100%;"
                    />
                  </el-col>

                  <el-col
                    :xs="24"
                    :sm="5"
                    :md="4"
                    :lg="3"
                    class="comment-button-box"
                  >
                    <el-button
                      type="danger"
                      :disabled="loading || !form.comment"
                      class="comment-button"
                      @click="mutate"
                    >
                      {{ $t('Comment') }}
                    </el-button>
                  </el-col>

                  <el-col
                    :xs="24"
                    :sm="7"
                    :md="6"
                    :lg="5"
                    class="attachment-box"
                  >
                    <el-upload
                      ref="attachmentsUpload"
                      class="attachment-upload"
                      action="#"
                      :auto-upload="false"
                      drag
                      multiple
                      :on-change="(file) => form.attachments.push(file.raw)"
                      :on-remove="handleRemove"
                    >
                      <i class="el-icon-upload" />
                      <div class="el-upload__text">
                        {{ $t('Drop file here or') }} <em>{{ $t('click to upload') }}</em>
                      </div>
                    </el-upload>
                  </el-col>
                </el-row>

                <el-divider class="new-comment-line" />
              </div>
            </template>
          </ApolloMutation>
        </el-footer>
      </el-container>
    </template>
  </ApolloQuery>
</template>
<script lang="ts">
import TicketCommentDistant from '@/components/Tickets/TicketCommentDistant.vue'
import TicketCommentSelf from '@/components/Tickets/TicketCommentSelf.vue'
import PackageDetails from '@/components/Tickets/PackageDetails.vue'
import Attachment from './Attachment.vue'
import AttachmentsList from './AttachmentsList.vue'
import Vue from 'vue'
import { Component, Watch } from 'vue-property-decorator'
import { formatDate } from '@/lib/date'
import {
  FetchTicketQuery,
  CreateTicketCommentQuery,
  SupportTicketStatus,
  TicketUpdatedQuery,
  ChangeTicketStatusQuery,
  TicketComment,
  TicketAttachment,
  TicketTag,
  UpdateTicketTagsQuery,
  MutationUpdateTicketTagsArgs,
  GetAvailableTicketsTagsQuery,
  PackageType,
  GetTicketTokenQuery,
  SupportTicket,
  AvailableSupportTicketTag,
  UserRole,
  Maybe,
  PackageByPackageNumberQuery
} from '@/types/graphql'
import UserModule from '@/store/modules/user'
import { getModule } from 'vuex-module-decorators'

interface PackageInfo {
  id: Maybe<string>
  type: Maybe<PackageType>
  packageNumber: Maybe<string>
}

@Component({
  components: {
    TicketCommentDistant,
    TicketCommentSelf,
    Attachment,
    AttachmentsList,
    PackageDetails
  }
})
export default class extends Vue {
  private get ticketId() {
    return this.$route.params.id
  }

  private userModule = getModule(UserModule, this.$store)

  private ticketQuery=FetchTicketQuery
  private ticketUpdatedSubscriptionQuery=TicketUpdatedQuery
  private ChangeTicketStatusQuery=ChangeTicketStatusQuery
  private CreateTicketCommentQuery = CreateTicketCommentQuery
  private updateTicketTagsQuery = UpdateTicketTagsQuery
  private packageByPackageNumberQuery = PackageByPackageNumberQuery
  private formatDate = formatDate
  private tagsOptions: AvailableSupportTicketTag[] = []
  private isLoadingTags=false;
  private newTagsInputValues = [];
  private form = {
    comment: '',
    attachments: [] as string[]
  }

  private updateTicketTagsArgs: MutationUpdateTicketTagsArgs = {
    ticketId: '',
    tags: []
  }

  @Watch('$route', { immediate: true })
  private routeChanged() {
    Vue.set(this.updateTicketTagsArgs, 'ticketId', this.ticketId)
    Vue.set(this.updateTicketTagsArgs, 'tags', [])
  }

  private ticketTagsCollapse = ['1']
  private attachmentsCollapse = ['1']

  private packageInfo: PackageInfo = {
    id: null,
    type: null,
    packageNumber: null
  }

  private newTagInputVisible = false

  get getUserEmail() {
    return this.userModule.email
  }

  private get name() {
    return this.userModule.name
  }

  private get isUserAdmin() {
    return this.userModule.roles.includes(UserRole.Admin)
  }

  private convertAnyToSupportTicket(data: any) {
    return data as SupportTicket
  }

  private showNewTagInput() {
    this.newTagInputVisible = true
    this.$nextTick(() => {
      (this.$refs.newTagInput as HTMLElement).focus()
    })
  }

  private handleNewTagInputConfirm(
    currentTags: Array<TicketTag>,
    mutateFn: () => void
  ) {
    this.updateTicketTagsArgs.tags = [...currentTags.map(tag => tag.name), ...this.newTagsInputValues]
    mutateFn()
    this.newTagInputVisible = false
    this.newTagsInputValues = []
  }

  private handleDeleteTag(currentTags: Array<TicketTag>, tagName: string) {
    this.updateTicketTagsArgs.tags = currentTags.map(tag => tag.name)
    this.updateTicketTagsArgs.tags.splice(this.updateTicketTagsArgs.tags.indexOf(tagName), 1)
  }

  private mergeAttachments(comments: TicketComment[]) {
    return comments.reduce(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      (arr, comment) => [...arr, ...comment.attachments!],
    [] as TicketAttachment[])
  }

  private getNewStatus(currentStatus: string) {
    return currentStatus === SupportTicketStatus.Closed || currentStatus === SupportTicketStatus.GiveUp
      ? SupportTicketStatus.Open : SupportTicketStatus.Closed
  }

  private getBoolTicketStatus(currentStatus: string) {
    return currentStatus !== SupportTicketStatus.Closed && currentStatus !== SupportTicketStatus.GiveUp
  }

  private statusBadgeType(status: SupportTicketStatus) {
    switch (status) {
      case 'new':
        return 'success'
      case 'open':
        return 'primary'
      case 'pending':
        return 'warning'
      case 'solved':
        return 'danger'
      case 'closed':
        return 'info'
      case 'merged':
        return 'info'
      case 'spam':
        return 'info'
    }
  }

  private internationalizeBadgeType(status: SupportTicketStatus) {
    switch (status) {
      case 'new':
        return this.$t('ticket.status.new')
      case 'open':
        return this.$t('ticket.status.open')
      case 'pending':
        return this.$t('ticket.status.pending')
      case 'solved':
        return this.$t('ticket.status.solved')
      case 'closed':
        return this.$t('ticket.status.closed')
      case 'merged':
        return this.$t('ticket.status.merged')
      case 'spam':
        return this.$t('ticket.status.spam')
      case 'give_up':
        return this.$t('ticket.status.give_up')
    }
  }

  private resetForm() {
    this.form.comment = ''
    this.form.attachments = [];
    (this.$refs.attachmentsUpload as any).clearFiles()
  }

  private handleRemove(file: {raw: string}) {
    const index = this.form.attachments.indexOf(file.raw, 0)
    if (index > -1) {
      this.form.attachments.splice(index, 1)
    }
  }

  private fetchTags(query: string) {
    this.isLoadingTags = true
    this.$apollo.query({ query: GetAvailableTicketsTagsQuery, variables: { search: query } }).then((
      { data }
    ) => {
      this.tagsOptions = data.getAvailableTicketsTags ? data.getAvailableTicketsTags : []
    }).catch(err => {
      console.error(err)
      return []
    })
      .finally(() => {
        this.isLoadingTags = false
      })
  }

  private handlePackageResult(packageResult: PackageByPackageNumberQuery['packageByPackageNumber']) {
    this.packageInfo.packageNumber = packageResult?.package_number || null
  }

  mounted() {
    this.fetchTags('')

    this.$apollo.query({
      query: GetTicketTokenQuery,
      variables: {
        id: this.ticketId
      }
    })
  }

  private packageDetailsFromTags(tagsList: Array<TicketTag>, info: string) {
    const packageString = '-package#'

    let details: Array<string> = []

    for (let i = 0; i < tagsList.length; i++) {
      if (tagsList[i].name.includes(packageString)) {
        details = tagsList[i].name.split(packageString)
      }
    }

    this.packageInfo.type = null

    if (details[0] === PackageType.Hgv.toLowerCase()) {
      this.packageInfo.type = PackageType.Hgv
    }

    if (details[0] === PackageType.Libert.toLowerCase()) {
      this.packageInfo.type = PackageType.Libert
    }

    if (details[0] === PackageType.Siemens.toLowerCase()) {
      this.packageInfo.type = PackageType.Siemens
    }

    this.packageInfo.packageNumber = details[1]?.toUpperCase() ?? null

    switch (info) {
      case 'type':
        return this.packageInfo.type
      case 'ID':
        return this.packageInfo.id
      case 'PACKAGE_NUMBER':
        return this.packageInfo.packageNumber
      default:
        return '-'
    }
  }
}
</script>

<style lang="scss">
@import "@/css/ticketCreateEdit.scss";
</style>
